import React, { useState } from 'react';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useFormContext, Controller } from 'react-hook-form';
import { get } from 'lodash';
import { cn } from '@src/utils/ui';
import PropTypes from 'prop-types';

const theme = createTheme({
    typography: {
        fontSize: 14,
        fontFamily: 'BCAASans, sans-serif'
    },
    palette: {
        primary: {
            main: '#0050c4'
        }
    },
    components: {
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '14px'
                }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    '&.Mui-focused': {
                        color: '#0050c4'
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#0050c4'
                    }
                }
            }
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    '&.Mui-focused': {
                        borderColor: '#0050c4'
                    }
                }
            }
        },
        MuiPickersDay: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    '&.Mui-selected': {
                        backgroundColor: '#0050c4',
                        '&:hover': {
                            backgroundColor: '#003ea3'
                        }
                    }
                }
            }
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                label: {
                    fontSize: '14px'
                },
                switchViewButton: {
                    '& svg': {
                        fontSize: '24px'
                    }
                },
                iconButton: {
                    '& svg': {
                        fontSize: '24px'
                    }
                }
            }
        },
        MuiPickersYear: {
            styleOverrides: {
                root: {
                    fontSize: '14px'
                },
                yearButton: {
                    fontSize: '14px',
                    '&.Mui-selected': {
                        backgroundColor: '#0050c4',
                        color: '#fff'
                    }
                }
            }
        },
        MuiPickersMonth: {
            styleOverrides: {
                root: {
                    fontSize: '14px',
                    '&.Mui-selected': {
                        backgroundColor: '#0050c4',
                        color: '#fff'
                    }
                }
            }
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    fontSize: '14px'
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '12px',
                    fontWeight: 400
                }
            }
        }
    }
});

export const DateInput = ({ name, InputProps = {}, FormHelperTextProps = {}, disabledDates, onChange, ...rest }) => {
    const { control, formState } = useFormContext();
    const error = !!get(formState.errors, name);
    const helperText = get(formState.errors, `${name}.message`);
    const [open, setOpen] = useState(false);

    const handleClick = e => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(true);
    };

    const isDateDisabled = date => {
        if (!disabledDates) return false;
        if (rest.maxDate && date.isAfter(rest.maxDate, 'day')) return true;
        const weekday = date.day();
        const iso = date.format('YYYY-MM-DD');

        return disabledDates.blockedDaysOfWeek?.includes(weekday) || disabledDates.blockedDates?.has(iso);
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <Controller
                    name={name}
                    control={control}
                    render={({ field }) => (
                        <DatePicker
                            open={open}
                            onOpen={() => setOpen(true)}
                            onClose={() => setOpen(false)}
                            value={field.value}
                            onChange={date => {
                                if (onChange) onChange(date);
                                field.onChange(date);
                            }}
                            shouldDisableDate={isDateDisabled}
                            {...rest}
                            slotProps={{
                                textField: {
                                    onClick: handleClick,
                                    error: error,
                                    helperText: helperText,
                                    InputProps: {
                                        ...InputProps,
                                        className: cn('tw-text-[14px]', InputProps.className)
                                    },
                                    SelectProps: { className: 'tw-text-[14px]' },
                                    InputLabelProps: { className: 'tw-text-[14px]' },
                                    FormHelperTextProps: {
                                        ...FormHelperTextProps,
                                        className: cn('tw-text-[12px]', FormHelperTextProps.className)
                                    }
                                }
                            }}
                            slots={{ openPickerIcon: () => null }}
                        />
                    )}
                />
            </LocalizationProvider>
        </ThemeProvider>
    );
};

DateInput.propTypes = {
    name: PropTypes.string.isRequired,
    InputProps: PropTypes.object,
    disabledDates: PropTypes.object,
    maxDates: PropTypes.object,
    FormHelperTextProps: PropTypes.object,
    onChange: PropTypes.func
};
